import debounce from 'lodash.debounce';
import { useEffect, useMemo, useRef } from 'react';

/**
 * Reduce the amount of calls to the given function, based on the delay. Reduces server load e.g. when binding calls to keystrokes (like autocomplete)
 * if the user stops typing, the functions wait at least {delay} ms before calling the function again
 */
const useDebounce = (callback: (...args: any[]) => void, delay: number = 1000) => {
  const callbackRef = useRef(callback);
  const debouncedFnRef = useRef<ReturnType<typeof debounce>>(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Create/Update the debounced function in an Effect (outside of render)
  useEffect(() => {
    const func = (...args: any[]) => {
      callbackRef.current?.(...args);
    };

    const debounced = debounce(func, delay);
    debouncedFnRef.current = debounced;

    // cancel the debounce if delay changes or component unmounts
    return () => {
      debounced.cancel();
    };
  }, [delay]);

  // Return a stable wrapper that delegates to the current debounced instance
  // This avoids accessing refs directly during the render phase definition
  const stableDebounce = useMemo(() => {
    const run = (...args: any[]) => {
      debouncedFnRef.current?.(...args);
    };

    // Optional: Proxy the clear/cancel method if your component uses it
    run.cancel = () => {
      debouncedFnRef.current?.cancel();
    };

    return run;
  }, []);

  return stableDebounce;
};

export default useDebounce;
